import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Shield, Truck, CreditCard, Lock, Building2, ArrowRight } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-vybe-600 via-vybe-500 to-coral-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-1">Ready to Transform?</h3>
              <p className="text-white/80">Shop premium keto supplements today.</p>
            </div>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-vybe-700 font-bold rounded-full hover:bg-slate-100 transition-all shadow-xl"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-vybe-500 to-vybe-700 rounded-xl flex items-center justify-center">
                <span className="text-white font-black text-xl">K</span>
              </div>
              <span className="font-display text-2xl font-black text-white">
                KETO<span className="text-vybe-400">VYBE</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Premium keto supplements for your wellness journey. Quality products, fast shipping, real results.
            </p>
            {/* Trust Badges */}
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-vybe-400" />
              </div>
              <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-coral-400" />
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="text-white font-bold mb-5">Shop</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/shop" className="text-slate-400 hover:text-vybe-400 transition-colors text-sm">
                  All Products
                </Link>
              </li>
              {categories.slice(0, 4).map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/shop?category=${cat.slug}`} 
                    className="text-slate-400 hover:text-vybe-400 transition-colors text-sm"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-bold mb-5">Info</h4>
            <ul className="space-y-3">
              {[
                { name: 'FAQ', path: '/faq' },
                { name: 'Ingredients', path: '/ingredients' },
                { name: 'How to Use', path: '/how-to-use' },
                { name: 'Why Choose Us', path: '/why-choose-us' },
                { name: 'Guarantee', path: '/guarantee' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-vybe-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-bold mb-5">Support</h4>
            <ul className="space-y-3">
              {[
                { name: 'Contact Us', path: '/contact' },
                { name: 'Shipping Info', path: '/shipping' },
                { name: 'Returns & Refunds', path: '/returns' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms & Conditions', path: '/terms' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-vybe-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-5">Contact</h4>
            <div className="space-y-4">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-3 text-slate-400 hover:text-vybe-400 transition-colors text-sm"
              >
                <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Phone className="w-4 h-4 text-vybe-400" />
                </div>
                {CONTACT_INFO.phone}
              </a>
              
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-3 text-slate-400 hover:text-vybe-400 transition-colors text-sm"
              >
                <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-cyan-400" />
                </div>
                {CONTACT_INFO.email}
              </a>
              
              <div className="flex items-start gap-3 text-slate-400 text-sm">
                <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-coral-400" />
                </div>
                <div>
                  <div>{CONTACT_INFO.address.street}</div>
                  <div>{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</div>
                </div>
              </div>

              {/* Company */}
              <div className="flex items-center gap-3 pt-3 mt-3 border-t border-slate-800">
                <div className="w-9 h-9 bg-vybe-600/20 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-vybe-400" />
                </div>
                <span className="text-white font-medium text-sm">{CONTACT_INFO.corpName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Payment Methods */}
          <div className="flex flex-col items-center mb-6">
            <div className="flex items-center gap-1 text-slate-500 text-xs mb-3">
              <CreditCard className="w-4 h-4" />
              Secure Payments
            </div>
            <div className="flex items-center gap-3">
              {['Visa', 'Mastercard', 'Amex', 'Discover'].map((card) => (
                <div key={card} className="px-3 py-1.5 bg-slate-800 rounded-lg text-slate-400 text-xs font-medium">
                  {card}
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-xs mt-3">
              Statement shows as "<span className="text-slate-400">{CARD_DESCRIPTOR}</span>"
            </p>
          </div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <p className="text-slate-500 text-sm">
                © {currentYear} Keto Vybe. All rights reserved.
              </p>
              <span className="hidden sm:inline text-slate-700">|</span>
              <span className="text-slate-600 text-sm">{CONTACT_INFO.corpName}</span>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <Link to="/privacy" className="text-slate-500 hover:text-vybe-400 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-slate-500 hover:text-vybe-400 transition-colors">
                Terms
              </Link>
              <Link to="/contact" className="text-slate-500 hover:text-vybe-400 transition-colors">
                Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
